import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, money, errMsg, DEFAULT_HOTEL_IMAGE } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LinkSimple, MapPin, Star, EnvelopeSimple, Copy } from "@phosphor-icons/react";

const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

export default function MyProperty({ hotels = [] }) {
  const [emailFor, setEmailFor] = useState(null);   // hotel object
  const [form, setForm] = useState({ to_email: "", client_name: "", message: "" });

  const intakeLink = useMutation({
    mutationFn: (hotelId) => api.createIntakeLink({ hotel_id: hotelId }),
    onSuccess: (res) => {
      const url = `${window.location.origin}/intake/${res.token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Requirement link copied — paste it into an email to your client");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const emailLink = useMutation({
    mutationFn: () => api.emailIntakeLink({
      hotel_id: emailFor.id,
      to_email: form.to_email.trim(),
      client_name: form.client_name.trim(),
      message: form.message.trim(),
      link_base: window.location.origin,
    }),
    onSuccess: (res) => {
      if (res.sent) {
        toast.success(`Requirement link emailed to ${res.delivered_to}`);
        setEmailFor(null);
      } else if (!res.email_configured) {
        navigator.clipboard?.writeText(res.url).catch(() => {});
        toast.error("Email isn't set up yet — link copied instead. Paste it to your client.");
        setEmailFor(null);
      } else {
        navigator.clipboard?.writeText(res.url).catch(() => {});
        toast.error("Couldn't deliver to that address (sending is in test mode) — link copied instead.");
        setEmailFor(null);
      }
    },
    onError: (e) => toast.error(errMsg(e, "Could not send email")),
  });

  const openEmail = (h) => { setForm({ to_email: "", client_name: "", message: "" }); setEmailFor(h); };
  const S = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="space-y-4" data-testid="my-property">
      <p className="text-sm text-[#4B5563]">Your assigned properties. Copy or email a client requirement link for any property, or manage its room types &amp; meeting rooms in the Function Diary tab.</p>
      {hotels.length === 0 && <p className="text-sm text-[#4B5563]">No properties assigned to you yet. Ask your admin to assign one.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {hotels.map((h) => (
          <div key={h.id} className="border border-[#E5E7EB]" data-testid={`my-property-${h.id}`}>
            <img src={h.images?.[0] || DEFAULT_HOTEL_IMAGE} alt={h.name} className="w-full h-40 object-cover" />
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="overline text-[#4B5563]">{h.brand || "Independent"}</span>
                {h.star_rating ? <span className="flex items-center gap-1 text-xs font-semibold"><Star size={12} weight="fill" color="#D97706" /> {h.star_rating}</span> : null}
              </div>
              <div className="font-display font-bold text-lg text-[#111827] mt-1">{h.name}</div>
              <div className="flex items-center gap-1 text-sm text-[#4B5563] mt-1"><MapPin size={13} weight="fill" /> {[...new Set([h.address, h.city, h.state].filter(Boolean))].join(", ")}</div>
              {h.base_rate ? <div className="font-mono font-semibold text-[#111827] mt-2">{money(h.base_rate)}/nt base</div> : null}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button data-testid={`my-property-reqlink-${h.id}`} onClick={() => intakeLink.mutate(h.id)} disabled={intakeLink.isPending}
                  className="inline-flex items-center justify-center gap-1.5 border border-[#002FA7] text-[#002FA7] px-3 py-2.5 text-sm font-semibold hover:bg-[#E0E7FF] transition-colors active:scale-[0.99] disabled:opacity-60">
                  <Copy size={16} weight="bold" /> Copy link
                </button>
                <button data-testid={`my-property-emaillink-${h.id}`} onClick={() => openEmail(h)}
                  className="inline-flex items-center justify-center gap-1.5 bg-[#002FA7] text-white px-3 py-2.5 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.99]">
                  <EnvelopeSimple size={16} weight="bold" /> Email link
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!emailFor} onOpenChange={(v) => !v && setEmailFor(null)}>
        <DialogContent className="rounded-none border-[#111827] max-w-lg" data-testid="email-link-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><EnvelopeSimple size={22} weight="bold" color="#002FA7" /> Email requirement link</DialogTitle></DialogHeader>
          {emailFor && <p className="text-sm text-[#4B5563] -mt-2">Send a secure requirement form for <b className="text-[#111827]">{emailFor.name}</b> to your client. Any enquiry they submit lands with you automatically.</p>}
          <div className="space-y-3">
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Client email *</span>
              <input data-testid="email-link-to" type="email" value={form.to_email} onChange={S("to_email")} placeholder="planner@company.com" className={inputCls} /></label>
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Client name (optional)</span>
              <input data-testid="email-link-name" value={form.client_name} onChange={S("client_name")} placeholder="Jane Smith" className={inputCls} /></label>
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Message (optional)</span>
              <textarea data-testid="email-link-message" rows={3} value={form.message} onChange={S("message")} placeholder="Great speaking with you — here's the link to send over your group details." className={inputCls} /></label>
          </div>
          <DialogFooter>
            <button data-testid="email-link-send" disabled={emailLink.isPending || !form.to_email.trim()} onClick={() => emailLink.mutate()}
              className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60 inline-flex items-center gap-2">
              <EnvelopeSimple size={16} weight="bold" /> {emailLink.isPending ? "Sending…" : "Send email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
